import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon,
  ViewStream as ViewStreamIcon,
  CallSplit as CallSplitIcon,
  ViewQuilt as ViewQuiltIcon
} from '@mui/icons-material';
import apiService from '../../services/api';

/**
 * ChunkSettings component for the admin panel
 * Allows configuring document chunking parameters for optimizing RAG performance
 */
function ChunkSettings() {
  // State for chunk settings
  const [settings, setSettings] = useState({
    chunkSize: 2000,
    chunkOverlap: 600,
    tableHandling: 'preserve',
    codeBlockHandling: 'preserve',
    minTokens: 50,
    maxTokens: 1600,
  });

  // State for UI feedback
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Get current settings when component mounts
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getChunkSettings();
      setSettings(response);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to load chunk settings: ${error.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Parse numeric values
    const parsedValue = ['chunkSize', 'chunkOverlap', 'minTokens', 'maxTokens'].includes(name)
      ? parseInt(value, 10)
      : value;
    
    setSettings(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Validate settings
    if (settings.chunkSize < settings.chunkOverlap) {
      setMessage({ 
        type: 'error', 
        text: 'Chunk size must be larger than chunk overlap' 
      });
      setSaving(false);
      return;
    }

    if (settings.minTokens > settings.maxTokens) {
      setMessage({ 
        type: 'error', 
        text: 'Minimum tokens must be less than maximum tokens' 
      });
      setSaving(false);
      return;
    }

    try {
      await apiService.updateChunkSettings(settings);
      setMessage({ 
        type: 'success', 
        text: 'Chunk settings updated successfully. New settings will apply to future document uploads.' 
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to update settings: ${error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle special handling selection
  const handleSpecialHandlingChange = (type, value) => {
    setSettings(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 0, mb: 3 }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ViewQuiltIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">Document Chunk Settings</Typography>
        </Box>
        <Tooltip title="Refresh settings">
          <IconButton onClick={fetchSettings} disabled={saving}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        {message.text && (
          <Alert 
            severity={message.type === 'success' ? 'success' : 'error'} 
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        <Typography variant="subtitle1" gutterBottom>
          Basic Chunking Parameters
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure how documents are split into chunks for embedding and retrieval.
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Chunk Size (characters)"
              name="chunkSize"
              type="number"
              value={settings.chunkSize}
              onChange={handleInputChange}
              InputProps={{ inputProps: { min: 500, max: 8000 } }}
              helperText="Number of characters per chunk"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Chunk Overlap (characters)"
              name="chunkOverlap"
              type="number"
              value={settings.chunkOverlap}
              onChange={handleInputChange}
              InputProps={{ inputProps: { min: 0, max: 2000 } }}
              helperText="Overlap between consecutive chunks"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Minimum Tokens"
              name="minTokens"
              type="number"
              value={settings.minTokens}
              onChange={handleInputChange}
              InputProps={{ inputProps: { min: 10, max: 200 } }}
              helperText="Minimum tokens for a valid chunk"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Maximum Tokens"
              name="maxTokens"
              type="number"
              value={settings.maxTokens}
              onChange={handleInputChange}
              InputProps={{ inputProps: { min: 500, max: 4000 } }}
              helperText="Maximum tokens per chunk"
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" gutterBottom>
          Special Content Handling
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure how special content like tables and code blocks are handled during document chunking.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TableChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Table Handling</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Choose how tables in documents should be processed during chunking.
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={settings.tableHandling}
                    onChange={(e) => handleSpecialHandlingChange('tableHandling', e.target.value)}
                  >
                    <MenuItem value="preserve">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TableChartIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="body2">Preserve Tables</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Keep tables intact within chunks
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="flatten">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ViewStreamIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="body2">Flatten Tables</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Convert tables to plain text
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="extract">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CallSplitIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="body2">Extract Tables</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Create separate chunks for tables
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText>Select table handling method</FormHelperText>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Code Block Handling</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Choose how code blocks in documents should be processed during chunking.
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={settings.codeBlockHandling}
                    onChange={(e) => handleSpecialHandlingChange('codeBlockHandling', e.target.value)}
                  >
                    <MenuItem value="preserve">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CodeIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="body2">Preserve Code</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Keep code blocks intact within chunks
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="extract">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CallSplitIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        <Box>
                          <Typography variant="body2">Extract Code</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Create separate chunks for code blocks
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText>Select code block handling method</FormHelperText>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary" 
            type="submit"
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default ChunkSettings;
